import {
  ClientNotification,
  ClientNotificationType,
  EmailPreference,
} from "../interfaces/clientNotification";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getNotifications = async (
  clientID: number,
): Promise<ClientNotification[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Notification/GetNotifications/${clientID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch notifications");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const getNotificationTypes = async (): Promise<
  ClientNotificationType[]
> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Notification/GetNotificationTypes`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch notification types");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notification types:", error);
    throw error;
  }
};

export const getEmailPreferences = async (
  clientID: number,
): Promise<EmailPreference[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Notification/GetEmailPreferences/${clientID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch email preferences");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching email preferences:", error);
    throw error;
  }
};

export const saveEmailPreferences = async (
  clientID: number,
  typeIDs: number[],
  emailFrequencyID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Notification/SaveEmailPreferences`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ clientID, typeIDs, emailFrequencyID }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to save preferences");
  }
};

export const markAsRead = async (notificationID: number) => {
  if (!apiBaseUrl) return;

  const token = localStorage.getItem("token");
  if (!token) {
    console.error("User not authenticated");
    return;
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/Notification/MarkAsRead`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(notificationID),
    });

    if (!response.ok) {
      throw new Error("Failed to mark notification as read");
    }
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

export const markAllAsRead = async (clientID: number) => {
  if (!apiBaseUrl) return;

  const token = localStorage.getItem("token");
  if (!token) {
    console.error("User not authenticated");
    return;
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Notification/MarkAllAsRead`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(clientID),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to mark notifications as read");
    }
  } catch (error) {
    console.error("Error marking notifications as read:", error);
  }
};
